import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import axios from "axios";
import {
  FileText,
  UploadCloud,
  Eye,
  X,
  Download
} from "lucide-react";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import '../Style/Documents.css'

const Documents = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [issued, setIssued] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
    fetchIssuedDocs();
  }, []);

  /* ================= FETCH MEMBERS ================= */

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      const society = localStorage.getItem("society");

      const resp = await axios.get(
        `${BASE_URL}/api/member/get-list?society=${encodeURIComponent(
          society
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMembers(resp.data || []);
    } catch {
      toast.error("Failed to fetch members");
    }
  };

  /* ================= FETCH DOCUMENTS ================= */

  const fetchIssuedDocs = async () => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const response = await axios.post(
        `${BASE_URL}/issued-docs/get-issued-docs`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { societyName },
        }
      );

      setIssued(response.data || []);
    } catch {
      toast.error("Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= FILE HANDLER ================= */

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setSelectedFile(file);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  /* ================= UPLOAD MULTI USER ================= */

  const handleUpload = async () => {
    if (!selectedFile || selectedUnits.length === 0) {
      toast.error("Select unit(s) and file");
      return;
    }

    setIsUploading(true);

    try {
      const societyName = localStorage.getItem("society");

      for (const unit of selectedUnits) {
        const formData = new FormData();

        formData.append("file", selectedFile);
        formData.append("unitNo", unit.value);
        formData.append("societyName", societyName);

        await axios.post(
          `${BASE_URL}/issued-docs/add-issued-docs`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      toast.success("Uploaded successfully");

      setSelectedFile(null);
      setPreviewUrl(null);
      setSelectedUnits([]);

      fetchIssuedDocs();
    } catch {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  /* ================= HELPERS ================= */

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filteredDocuments = issued.filter(
    (doc) =>
      doc.unitNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.fileName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app-container">
      <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="content-area">
          <SubHeader />

          <div className="documents-container documents-split-layout">

            {/* LEFT PANEL */}
            <div className="left-panel">
              <div className="upload-section-card">

                <h2 className="upload-section-title">
                  <UploadCloud size={22} style={{ color: "#007BFF" }} />
                  Upload Document
                </h2>

                <Select
                  options={members.map((m) => ({
                    value: m.unit_no,
                    label: `${m.unit_no} - ${m.member_name}`,
                  }))}
                  value={selectedUnits}
                  onChange={setSelectedUnits}
                  placeholder="Select unit(s)..."
                  isMulti
                />

                <div
                  className="file-drop-area"
                  onClick={() =>
                    document.getElementById("fileInput").click()
                  }
                >
                  <FileText size={40} />

                  <p>
                    {selectedFile
                      ? selectedFile.name
                      : "Click to select file"}
                  </p>

                  <input
                    type="file"
                    id="fileInput"
                    hidden
                    onChange={handleFileChange}
                  />
                </div>

                <button
                  className="upload-btn w-full"
                  onClick={handleUpload}
                  disabled={
                    !selectedFile ||
                    selectedUnits.length === 0 ||
                    isUploading
                  }
                >
                  {isUploading ? "Uploading..." : "Upload Document"}
                </button>
              </div>
            </div>

            {/* RIGHT PANEL (TABLE INSIDE CARD) */}
            <div className="right-panel">

              <div className="documents-table-card">
                <button
                  onClick={() => navigate("/documents-details")}
                  className="refresh-btn flex gap-2 items-center bg-blue-600 text-white hover:bg-blue-600"
                >
                  <Eye size={16} />
                  Member Uploads
                </button>


                {/* TABLE INSIDE CARD */}
                <div className="table-container">

                  <table className="documents-table">
                    <thead>
                      <tr>
                        <th>UNIT</th>
                        <th>FILE NAME</th>
                        {/* <th>TYPE</th> */}
                        <th>DATE</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredDocuments.map((doc, index) => (
                        <tr key={index}>
                          <td>{doc.unitNo}</td>
                          <td>{doc.fileName}</td>
                          <td>{formatDate(doc.createdDate)}</td>
                          <td>
                            {doc.fileType?.startsWith("image/") ? (
                              <button
                                className="btn-view"
                                onClick={() => {
                                  const base64Data =
                                    doc.fileData ||
                                    doc.imageDate ||
                                    doc.fileContent ||
                                    "";

                                  setModalImage(`data:${doc.fileType};base64,${base64Data}`);
                                  setShowImageModal(true);
                                }}
                              >
                                <Eye size={14} />
                                View
                              </button>
                            ) : (
                              <a
                                href={`data:${doc.fileType};base64,${doc.imageDate}`}
                                download={doc.fileName}
                                className="btn-view"
                              >
                                <Download size={14} />
                                Download
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>

          </div>

        </main>
      </div>

      <ToastContainer position="bottom-right" />
      {showImageModal && (
        <div
          className="image-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999
          }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="image-modal-content"
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "12px",
              position: "relative",
              maxWidth: "800px"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowImageModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                background: "transparent",
                cursor: "pointer"
              }}
            >
              <X size={24} />
            </button>

            <img
              src={modalImage}
              alt="Preview"
              style={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain"
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;